/**
 * <localization-selector> - "Shop Switch" paa Markets-arkitektur (THEME-SPEC 3, BUILD_PLAN 2.5).
 *
 * ÉT element, fire roller - hvilken en instans har afgoeres af hvad den indeholder,
 * ikke af en parameter. Snippet'en rendrer dem hver for sig (se dens kommentar):
 *   modal     <dialog> med flag-grid + soegefelt. Den rigtige vaelger.
 *   trigger   knappen der aabner modalen (footer + drawer).
 *   banner    foerste-besoegs anbefaling.
 *   selectors no-JS fallback i footeren - disclosures der kollapses naar JS er der.
 *
 * Triggeren og modalen er SOESKENDE i DOM, ikke indlejrede, saa aabningen gaar via
 * en dokument-delegeret lytter og en statisk instans-reference - samme moenster som
 * <menu-drawer>. showModal() giver focus-trap/Escape/inert baggrund nativt; vi
 * laegger focus-retur, aria-expanded og soegefiltreringen oven paa.
 *
 * To dele:
 *  1. Disclosure-vaelgere om {% form 'localization' %}. Hver mulighed er en aegte
 *     submit-knap -> valg submitter formen og serveren re-renderer i korrekt
 *     currency/locale. Penge/locale forbliver server-ejet (THEME-SPEC 2-3); intet
 *     genberegnes client-side. JS' eneste ansvar her er disclosure-mekanikken
 *     (aabne/lukke, tastatur, aria) - selve skiftet er en native form-submit.
 *  2. Foerste-besoegs ANBEFALING (ikke tvangs-redirect): det native
 *     browsing_context_suggestions.json (ingen tredjeparts-script) finder et bedre
 *     marked; banneret tilbyder skiftet, brugeren beslutter. Afvisning huskes i
 *     localStorage.
 *
 * Progressive enhancement: uden JS er elementet :not(:defined) -> listerne staar
 * aabne og submit-knapperne virker. Banneret er en ren enhancement (kraever fetch).
 */

const DISMISS_KEY = 'localization-recommendation-dismissed';

/** Én disclosure: knap der folder en liste af submit-muligheder ud. */
class Disclosure {
  constructor(root) {
    this.root = root;
    this.button = root.querySelector('[data-ls-button]');
    this.list = root.querySelector('[data-ls-list]');
    this.options = [...root.querySelectorAll('[data-ls-option]')];

    // JS til stede -> kollaps til disclosure (uden JS staar listen aaben via :not(:defined)).
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');

    this.onButtonClick = () => (this.open ? this.close() : this.show());
    this.onKeydown = this.#onKeydown.bind(this);
    this.onFocusOut = this.#onFocusOut.bind(this);
    this.onDocPointer = this.#onDocPointer.bind(this);

    this.button.addEventListener('click', this.onButtonClick);
    this.root.addEventListener('keydown', this.onKeydown);
    this.root.addEventListener('focusout', this.onFocusOut);
    // Ren disclosure: mulighederne er native, tab-bare submit-knapper. Tab/Shift+Tab
    // navigerer listen, Escape lukker - ingen roving tabindex / pile-navigation noedvendig
    // (ville goere widget'en menu-agtig og kollidere med role="list").
  }

  get open() {
    return this.button.getAttribute('aria-expanded') === 'true';
  }

  show() {
    this.list.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.onDocPointer);
    const current = this.options.find((option) => option.getAttribute('aria-current') === 'true');
    (current || this.options[0])?.focus();
  }

  close({ focusButton = false } = {}) {
    if (!this.open) return;
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.onDocPointer);
    if (focusButton) this.button.focus();
  }

  #onKeydown(event) {
    if (event.key === 'Escape' && this.open) {
      event.preventDefault();
      this.close({ focusButton: true });
    }
  }

  // Luk naar fokus forlader hele disclosuren (tastatur-tab eller klik andetsteds).
  #onFocusOut(event) {
    if (!this.root.contains(event.relatedTarget)) this.close();
  }

  #onDocPointer(event) {
    if (!this.root.contains(event.target)) this.close();
  }

  destroy() {
    this.button.removeEventListener('click', this.onButtonClick);
    this.root.removeEventListener('keydown', this.onKeydown);
    this.root.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('pointerdown', this.onDocPointer);
  }
}

class LocalizationSelector extends HTMLElement {
  connectedCallback() {
    this.disclosures = [...this.querySelectorAll('[data-ls-disclosure]')].map((root) => new Disclosure(root));

    this.dialog = this.querySelector('[data-ls-dialog]');
    if (this.dialog) this.#initModal();

    this.banner = this.querySelector('[data-ls-banner]');
    if (this.banner) {
      this.banner.addEventListener('click', (event) => {
        if (event.target.closest('[data-ls-dismiss]')) this.#dismiss();
      });
      // Anbefaling: ikke i editor, ikke hvis allerede afvist.
      if (!this.#designMode && !this.#dismissed) this.#maybeRecommend();
    }
  }

  disconnectedCallback() {
    this.disclosures?.forEach((disclosure) => disclosure.destroy());
    if (LocalizationSelector.instance === this) LocalizationSelector.instance = null;
  }

  /* ---------------- Modalen: den rigtige vaelger ---------------- */

  #initModal() {
    this.search = this.querySelector('[data-ls-search]');
    this.searchField = this.querySelector('[data-ls-search-field]');
    this.grid = this.querySelector('[data-ls-grid]');
    this.items = [...this.querySelectorAll('.localization-modal__item')];
    this.empty = this.querySelector('[data-ls-empty]');
    this.opener = null;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-ls-close]')) this.closeModal();
    });
    // Klik paa selve <dialog> (altsaa backdrop'en, ikke panelet) lukker.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeModal();
    });
    this.dialog.addEventListener('close', () => {
      this.#setTriggersExpanded(false);
      this.#resetSearch();
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    });

    if (this.search) {
      this.search.addEventListener('input', () => this.#filter());
      // Escape i et type=search toemmer feltet nativt i WebKit OG ville boble op
      // og lukke dialogen i samme tastetryk. Foerste Escape rydder soegningen,
      // anden lukker - saa man ikke mister modalen ved at fortryde en soegning.
      this.search.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.search.value) return;
        event.preventDefault();
        event.stopPropagation();
        this.#resetSearch();
      });
    }

    // Saet udgangstilstanden NU, ikke ved foerste tastetryk: markuppen sender alle
    // 33 lande med (det er no-JS-tilstanden, hvor de 24 ellers var uopnaaelige),
    // saa uden dette kald staar modalen aaben med praecis den flade liste
    // ejeren afviste. Maalt i harness: 33 synlige foer, 9 efter.
    this.#filter();

    LocalizationSelector.instance = this;
    if (!LocalizationSelector.documentBound) {
      document.addEventListener('click', LocalizationSelector.onTriggerClick);
      LocalizationSelector.documentBound = true;
    }
  }

  openModal(opener) {
    if (opener) this.opener = opener;
    if (!this.dialog.open) this.dialog.showModal();
    this.#setTriggersExpanded(true);
    // Fokus til soegefeltet: det er det der goer 33 lande til en beslutning.
    // Uden felt (single-country shop) lader vi showModal() om det.
    if (this.search) this.search.focus();
  }

  closeModal() {
    if (this.dialog.open) this.dialog.close();
  }

  #setTriggersExpanded(expanded) {
    document.querySelectorAll('[data-ls-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  #resetSearch() {
    if (!this.search) return;
    this.search.value = '';
    this.#filter();
    this.search.focus();
  }

  /**
   * Tom soegning viser kun DESTINATIONERNE (markeder med praecis ét land - de ni
   * med eget domaene og egen valuta). Skrives der noget, soeges der i hele
   * kataloget paa 33, ogsaa International-markedets 24, som ellers er skjult.
   * Matcher paa landenavn ELLER ISO-kode, saa baade "germ" og "de" rammer.
   */
  #filter() {
    const query = (this.search?.value || '').trim().toLowerCase();
    let shown = 0;

    for (const item of this.items) {
      const match = query ? LocalizationSelector.matches(item.dataset, query) : !('lsSecondary' in item.dataset);
      item.hidden = !match;
      if (match) shown += 1;
    }

    if (this.empty) this.empty.hidden = shown > 0;
    this.#announce(shown, query);
  }

  /**
   * ORD-praefiks, ikke substring. En ren `includes` lod "de" traekke Sweden med
   * (sw-ede-n) og "an" naesten hele kataloget - maalt i harness. Her matcher et
   * land hvis NAVNET eller et af dets ord starter med query, eller hvis
   * ISO-koden goer ("de" -> Denmark + Germany, "kingdom" -> United Kingdom).
   */
  static matches({ lsName, lsCode }, query) {
    if (lsCode.startsWith(query)) return true;
    return lsName.split(' ').some((word) => word.startsWith(query));
  }

  #announce(shown, query) {
    const live = this.querySelector('[data-ls-live]');
    if (!live) return;
    // Kun mens der soeges: en melding ved aabning ville tale oven i dialogens
    // eget navn. Tom soegning nulstiller omraadet i stedet.
    if (!query) {
      live.textContent = '';
      return;
    }
    if (shown === 0) {
      live.textContent = this.empty?.textContent.trim() || '';
      return;
    }
    // Ental/flertal kommer som to faerdige strenge fra serveren: Liquid kan ikke
    // vaelge pluralform, naar antallet foerst kendes her. ("1 countries shown"
    // var det den ene skabelon gav.)
    const template = shown === 1 ? live.getAttribute('data-count-one') : live.getAttribute('data-count-other') || '';
    live.textContent = (template || '').replace('[count]', shown);
  }

  static onTriggerClick(event) {
    const trigger = event.target.closest('[data-ls-open]');
    if (!trigger) return;
    const selector = LocalizationSelector.instance;
    if (!selector) return; // ingen modal i DOM -> triggeren er skjult af CSS alligevel
    event.preventDefault();
    selector.openModal(trigger);
  }

  get #designMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  get #dismissed() {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }

  #dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode: skjul bare for denne side */
    }

    // Fokus staar paa Dismiss-knappen INDE i banneret. Skjuler vi det uden videre,
    // falder fokus til <body> og naeste Tab starter forfra paa skip-linket (WCAG
    // 2.4.3 Focus Order). Banneret er sidst i DOM, saa der er intet "efter" at
    // rykke til - <main> er stedet brugeren netop valgte at komme videre til ved
    // at afvise anbefalingen, og den baerer allerede tabindex="-1" i theme.liquid.
    // Flyttes FOER hidden saettes: fokus paa et skjult element er ikke tilladt.
    const main = document.getElementById('main');
    if (main) main.focus({ preventScroll: true });

    this.banner.hidden = true;

    // Ryd status-omraadet, saa anbefalingen ikke bliver staaende som "seneste
    // melding" for en skaermlaeser der navigerer tilbage til den.
    const live = this.querySelector('[data-ls-live]');
    if (live) live.textContent = '';
  }

  async #maybeRecommend() {
    const shopify = window.Shopify || {};
    const root = (shopify.routes && shopify.routes.root) || '/';
    const currentCountry = shopify.country || '';
    const currentLanguage = shopify.language || '';
    const url =
      `${root}browsing_context_suggestions.json` +
      `?country[enabled]=true&country[exclude]=${encodeURIComponent(currentCountry)}` +
      `&language[enabled]=true&language[exclude]=${encodeURIComponent(currentLanguage)}`;

    let data;
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      data = await response.json();
    } catch {
      return; // stille: anbefaling maa aldrig blokere eller kaste
    }

    const suggestion = Array.isArray(data?.suggestions) ? data.suggestions[0] : null;
    const parts = suggestion?.parts;
    if (!parts) return;

    // Anbefal kun en dimension butikken FAKTISK kan skifte (vaelgeren findes i DOM)
    // og som adskiller sig fra nuvaerende kontekst. Et single-language shop har ingen
    // sprog-vaelger -> sprog-forslaget droppes (ellers "Shop in English?" mens man ER
    // paa engelsk). Handle-sammenligningen beskytter ogsaa mod tomt Shopify.language,
    // hvor API'ets exclude svigter og det foreslaar det aktive sprog.
    //
    // Soeges i DOKUMENTET, ikke i `this`: banner-instansen er sin egen
    // <localization-selector> i theme.liquid og baerer INGEN vaelgere selv (fixed
    // banner kan ikke bo i footeren eller i drawer-dialogen - se snippet'ens
    // kommentar). Spurgte vi kun os selv, ville svaret altid vaere "kan ikke
    // skifte" og banneret aldrig vises. Dimensionen markeres med data-attributter
    // og ikke id, fordi id'erne er praefikset pr. instans.
    const country =
      document.querySelector('[data-ls-country]') && parts.country && parts.country.handle !== currentCountry
        ? parts.country
        : null;
    const language =
      document.querySelector('[data-ls-language]') && parts.language && parts.language.handle !== currentLanguage
        ? parts.language
        : null;

    if (!country && !language) return; // intet handlingsbart at foreslaa

    this.#revealBanner(country, language);
  }

  #revealBanner(country, language) {
    const shopify = window.Shopify || {};
    const setValue = (selector, value) => {
      const field = this.banner.querySelector(selector);
      if (field && value != null) field.value = value;
    };
    // Skift-formen baerer BEGGE dimensioner. Den der ikke skiftes saettes til sin
    // NUVAERENDE vaerdi (ikke tom) - et tomt country_code/language_code ved submit
    // kan nulstille locale. Serveren beholder saa korrekt den uaendrede dimension.
    setValue('[name="country_code"]', country ? country.handle : shopify.country || '');
    setValue('[name="language_code"]', language ? language.handle : shopify.language || '');

    const text = this.banner.querySelector('[data-ls-banner-text]');
    if (text) {
      // Byg beskeden af uafhaengige led: kun de dimensioner der faktisk foreslaas
      // bidrager med en saetning. Saa grammatikken holder for country-only,
      // language-only OG begge - ingen tom "[country]"-rest i teksten.
      const clauses = [];
      if (country) {
        clauses.push((text.getAttribute('data-country-template') || '').replaceAll('[country]', country.name));
      }
      if (language) {
        clauses.push((text.getAttribute('data-language-template') || '').replaceAll('[language]', language.name));
      }
      text.textContent = clauses.filter(Boolean).join(' ');
    }

    this.banner.hidden = false;

    const live = this.querySelector('[data-ls-live]');
    if (live && text) live.textContent = text.textContent;
  }
}

if (!customElements.get('localization-selector')) {
  customElements.define('localization-selector', LocalizationSelector);
}
